import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, CheckCircle, Info, Search, X, XCircle } from 'lucide-react';

/**
 * Dialogs for Document Processing that replace window.prompt / window.confirm /
 * window.alert on the import-to-OMS path. A typed prompt let a portfolio be
 * chosen by prefix match with no read-back (wrong-book risk into IBOR), and the
 * import result lived only in an alert that vanished on dismiss.
 */

export interface PortfolioOption {
  id: number;
  portfolioCode: string;
  portfolioName: string;
}

/** What the picker resolves to: a portfolio code, an explicit block trade, or cancel. */
export type PortfolioChoice = { kind: 'portfolio'; code: string } | { kind: 'block' } | null;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const Panel = styled.div`
  background: #1e293b;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  width: min(620px, 92vw);
  max-height: 84vh;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  h3 { margin: 0; font-size: 1.1rem; font-weight: 600; color: #fff; }
  p { margin: 0.25rem 0 0; font-size: 0.8rem; color: #94a3b8; }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.25rem;
  &:hover { color: #fff; }
`;

const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 8px;
  padding: 0.55rem 0.75rem;
  color: #94a3b8;
  &:focus-within { border-color: #60a5fa; }
  input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: #e2e8f0;
    font-size: 0.9rem;
  }
`;

const List = styled.div`
  overflow-y: auto;
  max-height: 44vh;
  border: 1px solid #334155;
  border-radius: 8px;
`;

const Row = styled.button<{ $selected?: boolean }>`
  display: flex;
  width: 100%;
  gap: 0.75rem;
  align-items: baseline;
  text-align: left;
  background: ${p => (p.$selected ? 'rgba(59, 130, 246, 0.18)' : 'transparent')};
  border: none;
  border-bottom: 1px solid #1e293b;
  border-left: 3px solid ${p => (p.$selected ? '#60a5fa' : 'transparent')};
  padding: 0.6rem 0.75rem;
  color: #e2e8f0;
  cursor: pointer;
  font-size: 0.85rem;
  &:hover { background: rgba(59, 130, 246, 0.1); }
  code { color: #93c5fd; font-size: 0.8rem; min-width: 9rem; }
  span { color: #cbd5e1; }
  small { color: #94a3b8; }
`;

const Empty = styled.div`
  padding: 1rem;
  color: #94a3b8;
  font-size: 0.85rem;
`;

const ReadBack = styled.div<{ $tone: 'ok' | 'warn' | 'none' }>`
  border-radius: 8px;
  padding: 0.65rem 0.8rem;
  font-size: 0.85rem;
  border: 1px solid ${p => (p.$tone === 'ok' ? '#166534' : p.$tone === 'warn' ? '#92400e' : '#334155')};
  background: ${p => (p.$tone === 'ok' ? 'rgba(22, 101, 52, 0.18)' : p.$tone === 'warn' ? 'rgba(146, 64, 14, 0.18)' : 'transparent')};
  color: ${p => (p.$tone === 'none' ? '#94a3b8' : '#e2e8f0')};
  strong { color: #fff; }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
`;

const Btn = styled.button<{ $primary?: boolean }>`
  border-radius: 8px;
  padding: 0.55rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${p => (p.$primary ? '#2563eb' : '#475569')};
  background: ${p => (p.$primary ? '#2563eb' : 'transparent')};
  color: ${p => (p.$primary ? '#fff' : '#cbd5e1')};
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const TextArea = styled.textarea`
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  color: #e2e8f0;
  font-size: 0.9rem;
  min-height: 5.5rem;
  resize: vertical;
  &:focus { outline: none; border-color: #60a5fa; }
`;

const normalize = (text: string) => text.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Choose the portfolio an imported document books into. Nothing is chosen by
 * default: the person picks a row (or explicitly picks "no portfolio") and the
 * choice is read back in full before Import is enabled.
 */
export const PortfolioPickerDialog: React.FC<{
  docType?: string;
  documentName?: string;
  portfolios: PortfolioOption[];
  onResolve: (choice: PortfolioChoice) => void;
}> = ({ docType, documentName, portfolios, onResolve }) => {
  const [query, setQuery] = useState('');
  const [choice, setChoice] = useState<PortfolioChoice>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => { searchRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onResolve(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onResolve]);

  const matches = useMemo(() => {
    const key = normalize(query);
    if (!key) return portfolios;
    return portfolios.filter(p =>
      normalize(p.portfolioCode).includes(key) || normalize(p.portfolioName).includes(key));
  }, [portfolios, query]);

  const selected = choice?.kind === 'portfolio'
    ? portfolios.find(p => p.portfolioCode === choice.code)
    : undefined;

  const onSearchKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && matches.length === 1) {
      event.preventDefault();
      setChoice({ kind: 'portfolio', code: matches[0].portfolioCode });
    }
  };

  return (
    <Overlay onClick={() => onResolve(null)}>
      <Panel role="dialog" aria-modal="true" aria-labelledby="pick-portfolio-title" onClick={e => e.stopPropagation()}>
        <Head>
          <div>
            <h3 id="pick-portfolio-title">Which portfolio does this book into?</h3>
            <p>{documentName || docType || 'This document'} creates a DRAFT trade in the portfolio you choose.</p>
          </div>
          <IconButton type="button" aria-label="Cancel import" onClick={() => onResolve(null)}><X size={18} /></IconButton>
        </Head>
        <SearchBox>
          <Search size={16} />
          <input
            ref={searchRef}
            value={query}
            placeholder="Search by portfolio code or name"
            aria-label="Search portfolios"
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onSearchKey}
          />
        </SearchBox>
        <List role="listbox" aria-label="Portfolios">
          {matches.length === 0 && <Empty>No portfolio matches “{query}”.</Empty>}
          {matches.map(p => (
            <Row
              key={p.portfolioCode}
              type="button"
              role="option"
              aria-selected={selected?.portfolioCode === p.portfolioCode}
              $selected={selected?.portfolioCode === p.portfolioCode}
              onClick={() => setChoice({ kind: 'portfolio', code: p.portfolioCode })}
              onDoubleClick={() => onResolve({ kind: 'portfolio', code: p.portfolioCode })}
            >
              <code>{p.portfolioCode}</code>
              <span>{p.portfolioName}</span>
            </Row>
          ))}
          <Row
            type="button"
            role="option"
            aria-selected={choice?.kind === 'block'}
            $selected={choice?.kind === 'block'}
            onClick={() => setChoice({ kind: 'block' })}
          >
            <code>No portfolio</code>
            <small>Create a DRAFT block trade and allocate it to portfolios later</small>
          </Row>
        </List>
        <ReadBack $tone={selected ? 'ok' : choice?.kind === 'block' ? 'warn' : 'none'} aria-live="polite">
          {selected && <>Books into <strong>{selected.portfolioCode}</strong> — {selected.portfolioName}</>}
          {choice?.kind === 'block' && <>Books as an <strong>unallocated DRAFT block trade</strong>. It stays out of every portfolio until you allocate it.</>}
          {!choice && 'Choose a portfolio, or choose “No portfolio” for a block trade.'}
        </ReadBack>
        <Actions>
          <Btn type="button" onClick={() => onResolve(null)}>Cancel</Btn>
          <Btn type="button" $primary disabled={!choice} onClick={() => onResolve(choice)}>Import</Btn>
        </Actions>
      </Panel>
    </Overlay>
  );
};

/** A required free-text reason, e.g. why a source is authoritative. */
export const ReasonDialog: React.FC<{
  title: string;
  description?: string;
  confirmLabel?: string;
  onResolve: (reason: string | null) => void;
}> = ({ title, description, confirmLabel = 'Save', onResolve }) => {
  const [reason, setReason] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onResolve(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onResolve]);
  return (
    <Overlay onClick={() => onResolve(null)}>
      <Panel role="dialog" aria-modal="true" aria-labelledby="reason-title" onClick={e => e.stopPropagation()}>
        <Head>
          <div>
            <h3 id="reason-title">{title}</h3>
            {description && <p>{description}</p>}
          </div>
          <IconButton type="button" aria-label="Cancel" onClick={() => onResolve(null)}><X size={18} /></IconButton>
        </Head>
        <TextArea ref={ref} value={reason} aria-label={title} onChange={e => setReason(e.target.value)} />
        <Actions>
          <Btn type="button" onClick={() => onResolve(null)}>Cancel</Btn>
          <Btn type="button" $primary disabled={!reason.trim()} onClick={() => onResolve(reason.trim())}>{confirmLabel}</Btn>
        </Actions>
      </Panel>
    </Overlay>
  );
};

export interface ImportOutcome {
  jobId: string;
  documentName?: string;
  tone: 'success' | 'warning' | 'error';
  title: string;
  lines: string[];
  nextStep?: string;
  warning?: string;
  /** Show a link to the trade blotter (DRAFT tickets need their next step there). */
  openBlotter?: boolean;
  /** F-MK-076: where the imported record now lives, e.g. the collection period an agent statement landed on. */
  openLink?: { label: string; to: string };
}

const OutcomeCard = styled.div<{ $tone: ImportOutcome['tone'] }>`
  border-radius: 12px;
  padding: 1rem 1.1rem;
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  border: 1px solid ${p => (p.$tone === 'success' ? '#166534' : p.$tone === 'warning' ? '#92400e' : '#991b1b')};
  background: ${p => (p.$tone === 'success' ? 'rgba(22, 101, 52, 0.16)' : p.$tone === 'warning' ? 'rgba(146, 64, 14, 0.16)' : 'rgba(153, 27, 27, 0.18)')};
  color: #e2e8f0;
  font-size: 0.85rem;
  h4 { margin: 0; font-size: 0.95rem; color: #fff; }
  ul { margin: 0.4rem 0 0; padding-left: 1.1rem; color: #cbd5e1; }
  p { margin: 0.45rem 0 0; }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #93c5fd;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
`;

/**
 * The result of the last import, kept on screen until dismissed so the trade id
 * and the next step are not lost with an alert.
 */
export const ImportOutcomePanel: React.FC<{
  outcome: ImportOutcome;
  onDismiss: () => void;
  onOpenBlotter: () => void;
  onOpenLink?: (to: string) => void;
}> = ({ outcome, onDismiss, onOpenBlotter, onOpenLink }) => {
  const Icon = outcome.tone === 'success' ? CheckCircle : outcome.tone === 'warning' ? AlertTriangle : XCircle;
  const color = outcome.tone === 'success' ? '#4ade80' : outcome.tone === 'warning' ? '#fbbf24' : '#f87171';
  return (
    <OutcomeCard $tone={outcome.tone} role="status">
      <Icon size={20} color={color} style={{ flex: '0 0 auto', marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <h4>{outcome.title}</h4>
        {outcome.documentName && <div style={{ color: '#94a3b8', fontSize: '0.78rem', marginTop: 2 }}>{outcome.documentName}</div>}
        {outcome.lines.length > 0 && <ul>{outcome.lines.map(line => <li key={line}>{line}</li>)}</ul>}
        {outcome.nextStep && (
          <p><Info size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />{outcome.nextStep}
            {outcome.openBlotter && <> <LinkButton type="button" onClick={onOpenBlotter}>Open the Trade Blotter</LinkButton></>}
          </p>
        )}
        {outcome.openLink && onOpenLink && <p><LinkButton type="button" onClick={() => onOpenLink(outcome.openLink!.to)}>{outcome.openLink.label}</LinkButton></p>}
        {outcome.warning && <p style={{ color: '#fcd34d' }}><AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />{outcome.warning}</p>}
      </div>
      <IconButton type="button" aria-label="Dismiss import result" onClick={onDismiss}><X size={18} /></IconButton>
    </OutcomeCard>
  );
};
